import { useEffect, useState } from 'react';
import { Languages, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const STORAGE_KEY = 'mt-notice-dismissed-v1';

// Tiny floating pill shown on every page when the user is viewing a non-FR
// language. Tells the visitor the content is machine-translated (via Gemini)
// and dismissible — the dismissal is per-language so switching to another
// foreign language re-prompts once.
export const MachineTranslationNotice = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'fr';
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!lang || lang.startsWith('fr')) {
      setVisible(false);
      return;
    }
    try {
      const dismissed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      setVisible(!dismissed.includes(lang));
    } catch {
      setVisible(true);
    }
  }, [lang]);

  if (!visible) return null;

  const dismiss = () => {
    try {
      const dismissed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      if (!dismissed.includes(lang)) dismissed.push(lang);
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(dismissed));
    } catch {
      /* best-effort */
    }
    setVisible(false);
  };

  // Per-language label so the pill itself is localized without requiring
  // Gemini at first paint. Fallback to English for unsupported codes.
  const LABEL = {
    en: 'Automatic translation — please excuse any approximation.',
    es: 'Traducción automática — disculpe cualquier aproximación.',
    it: 'Traduzione automatica — ci scusiamo per eventuali imprecisioni.',
    de: 'Automatische Übersetzung — wir bitten um Verständnis bei Ungenauigkeiten.',
    zh: '自动翻译，如有不当请见谅。',
    ru: 'Автоматический перевод — приносим извинения за возможные неточности.',
    ht: 'Tradiksyon otomatik — eskize nou pou nenpòt apwoksimasyon.',
  };
  const DISMISS = {
    en: 'Got it', es: 'Entendido', it: 'Capito', de: 'Verstanden',
    zh: '我知道了', ru: 'Понятно', ht: 'Konprann',
  };
  const label = LABEL[lang.slice(0, 2)] || LABEL.en;
  const dismissLabel = DISMISS[lang.slice(0, 2)] || DISMISS.en;

  return (
    <div
      className="fixed bottom-4 right-4 z-[55] max-w-sm pointer-events-auto"
      data-testid="machine-translation-notice"
      role="status"
      aria-live="polite"
    >
      <div className="bg-[#1F2A4A]/95 backdrop-blur-md text-[#FAF7F0] rounded-2xl shadow-[0_18px_45px_-15px_rgba(31,42,74,0.55)] border border-white/10 px-4 py-3 flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-[#C9B896]/20 text-[#C9B896] flex items-center justify-center shrink-0">
          <Languages className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0 text-[12px] leading-snug">
          <p className="text-white/90">{label}</p>
          <button
            type="button"
            onClick={dismiss}
            className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-[#C9B896] hover:text-white transition-colors"
            data-testid="machine-translation-notice-dismiss"
          >
            {dismissLabel} →
          </button>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="text-white/55 hover:text-white transition-colors shrink-0"
          aria-label={dismissLabel}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default MachineTranslationNotice;
